import { AppealNotRequiredPayload } from "../../../api/postCall/appealNotRequired";
import { SendAppealPayload } from "../../../api/postCall/sendAppeal";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";

/** Field used to populate `mrn` on appeal generate requests. */
export type AppealMrnSource = "patient_mrn" | "sequence";

/**
 * Resolves the MRN value for appeal generate payloads from a table row.
 *
 * @param rowData - Formatted table row for the denied case
 * @param mrnSource - Whether to read `patient_mrn` (NYCBS STP) or sequence (Astera oncology)
 * @returns Trimmed MRN string for the generate API
 */
export const resolveAppealMrn = (
  rowData: OncologySubmissionTableDataModel,
  mrnSource: AppealMrnSource = "sequence",
): string => {
  if (mrnSource === "patient_mrn") {
    return String(rowData.patient_mrn ?? "").trim();
  }

  return String(rowData.sequence ?? rowData.seq ?? "").trim();
};

/**
 * Validates whether Send to Appeal can open the send/generate modal.
 *
 * @param rowData - Formatted oncology submission table row
 * @returns True when the badge is an enabled Send control
 */
export const canOpenSendToAppealModal = (
  rowData: OncologySubmissionTableDataModel,
): boolean => {
  const badge = rowData.sendToAppeal;
  return Boolean(badge && !badge.isDisabled && badge.text === "Send");
};

/**
 * Validates whether Send to Appeal can open the appeal detail view modal.
 *
 * @param rowData - Formatted table row with `sendToAppeal` badge state
 * @returns True when the badge is an enabled View control
 */
export const canOpenAppealDetailsModal = (
  rowData: OncologySubmissionTableDataModel,
): boolean => {
  const badge = rowData.sendToAppeal;
  return Boolean(badge && !badge.isDisabled && badge.text === "View");
};

/**
 * Builds the appeal generate payload from a table row.
 *
 * @param rowData - Formatted table row for the denied case
 * @param mrnSource - Whether to read `patient_mrn` (NYCBS STP) or sequence (Astera oncology)
 * @returns Request body for `pharmacy/appeal/generate`
 */
export const buildGenerateAppealPayload = (
  rowData: OncologySubmissionTableDataModel,
  mrnSource: AppealMrnSource = "sequence",
) => ({
  identifier: String(rowData.identifier ?? "").trim(),
  mrn: resolveAppealMrn(rowData, mrnSource),
  drug_description: rowData.drug?.trim() || undefined,
  plan_name: rowData.plan_name?.trim() || rowData.pharmacy?.trim() || undefined,
});

/**
 * Builds the appeal send payload from an oncology submission table row.
 *
 * @param rowData - Formatted table row for the denied case
 * @param appealReason - Optional user-entered appeal reason from the Send modal
 * @returns Request body for `pharmacy/appeal/send`
 */
export const buildSendAppealPayload = (
  rowData: OncologySubmissionTableDataModel,
  appealReason?: string | null,
): SendAppealPayload => ({
  identifier: String(rowData.identifier ?? "").trim(),
  appeal_type: "Fax",
  appeal_reason:
    appealReason?.trim() || rowData.pa_status_reason?.trim() || undefined,
});

/**
 * Builds the appeal-not-required payload from a table row and user reason.
 *
 * @param rowData - Formatted table row for the denied case
 * @param reason - User-provided reason the appeal is not required
 * @returns Request body for `pharmacy/appeal/not-required`
 */
export const buildAppealNotRequiredPayload = (
  rowData: OncologySubmissionTableDataModel,
  reason: string,
): AppealNotRequiredPayload => ({
  identifier: String(rowData.identifier ?? "").trim(),
  reason: reason.trim(),
});
